import React from 'react';
import { injectIntl, defineMessages } from 'react-intl';
import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { connect } from 'react-redux';
import Avatar from 'react-avatar';
import JobStatusIcon from './JobStatusIcon';
import AddressController from '../Common/AddressController';
import * as CONST from '../Common/constants';
import AISuggestionsIcon from './CrossRank/AISuggestionsIcon';
import Box from '@material-ui/core/Box';

const useStyles = (theme) => ({
  listbox: {
    maxWidth: '100%',
    overflowX: 'hidden',
    maxHeight: 'calc(65vh - 100px)'
  },
  popper: {
    maxWidth: '100%'
  },

  mainButtonContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column'
  },

  filterLabel: {
    display: 'flex',
    marginBottom: '0.25rem'
  },
  inputRoot: {
    '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
      padding: '10.5px 14px 10.5px'
    },
    '&[class*="MuiOutlinedInput-root"] .MuiAutocomplete-endAdornment': {
      paddingRight: '0.5rem'
    },
    fontSize: 14,
    color: theme.palette.gray.darkerMedium,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    paddingTop: '0px !important',
    paddingBottom: '0px !important',
    marginTop: '0px !important',
    marginBottom: '0px !important',
    marginLeft: theme.spacing(0.5),
    fontFamily: 'Roboto !important',
    fontWeight: '400 !important',
    '& input': {
      overflow: 'hidden !important',
      maxWidth: '90% !important',
      textOverflow: 'ellipsis !important',
      whiteSpace: 'nowrap !important',
      fontSize: 14,
      color: theme.palette.gray.darkerMedium,
      paddingTop: '0px !important',
      paddingBottom: '0px !important',
      marginTop: '0px !important',
      marginBottom: '0px !important',
      marginLeft: theme.spacing(0.5),
      fontFamily: 'Roboto !important',
      fontWeight: '400 !important'
    }
  },
  containerOption: {
    width: '100% !important',
    maxWidth: '100% !important'
  },
  headerOption: {
    display: 'flex',
    alignItems: 'center',
    width: '100% !important',
    maxWidth: '100% !important'
  },
  bodyOption: {
    display: 'flex',
    alignItems: 'center',
    width: '100% !important',
    maxWidth: '100% !important',
    marginLeft: theme.spacing(0.5)
  },
  option: {
    borderTop: `1px solid ${theme.palette.gray.main}`,
    width: '100% !important',
    paddingTop: `${theme.spacing(0.5)}px !important`,
    paddingBottom: `${theme.spacing(0.3)}px !important`,
    textTransform: 'none'
  },
  jobCode: {
    fontSize: 14,
    color: theme.palette.gray.darkerMedium,
    paddingTop: '0px !important',
    paddingBottom: '0px !important',
    marginTop: '0px !important',
    marginBottom: '0px !important',
    fontFamily: 'Roboto',
    fontWeight: 400
  },
  divider: {
    marginLeft: theme.spacing(0.5)
  },
  jobTitle: {
    fontSize: 14,
    color: theme.palette.gray.darkerMedium,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    paddingTop: '0px !important',
    paddingBottom: '0px !important',
    marginTop: '0px !important',
    marginBottom: '0px !important',
    marginLeft: theme.spacing(0.5),
    fontFamily: 'Roboto',
    fontWeight: 400
  },
  jobStatusIcon: {
    fontSize: 20,
    marginRight: theme.spacing(0.5)
  },
  containerUserRecruiter: {
    display: 'flex',
    alignItems: 'center'
  },
  avatar: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5)
  },
  textCompanyAreaAndLocation: {
    color: theme.palette.gray.darkerRegular,
    fontSize: 10,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    paddingTop: '0px !important',
    paddingBottom: '0px !important',
    marginTop: '0px !important',
    marginBottom: '0px !important',
    fontFamily: 'Roboto'
  },
  circle: {
    fontSize: '14px',
    color: theme.palette.gray.darkerRegular,
    fontWeight: 400
  }
});

const messages = defineMessages({
  loading: {
    id: 'AutoCompleteJobSelect.loading',
    defaultMessage: 'Loading...'
  },
  noOptions: {
    id: 'AutoCompleteJobSelect.noOptions',
    defaultMessage: 'No job found'
  }
});

// Memoized option component to prevent unnecessary re-renders
const RenderOption = React.memo(({ option, classes, getStatusLabel, countries, intl }) => {
  const userRecruiter = option?.userRecruiter;
  const jobStatusLabel = getStatusLabel(option?.status);

  const avatarSrc =
    userRecruiter && userRecruiter?.avatar_50_path
      ? `https://s3-sa-east-1.amazonaws.com/recrutai-dev/${userRecruiter?.avatar_50_path}`
      : '';

  const companyAreaName = option?.companyArea?.name || option?.company_area_name || '';

  let addressFormatted = '';
  if (option?.address) {
    try {
      const mockContext = {
        props: {
          countries: countries || [],
          intl: intl
        }
      };
      addressFormatted = AddressController.JobAddressFormatterNew(mockContext, option);
    } catch (e) {
      addressFormatted = '';
    }
  }

  return (
    <div className={classes.containerOption}>
      <div className={classes.headerOption}>
        <div className={classes.jobStatusIcon}>
          <JobStatusIcon item={option} />
        </div>

        <p className={classes.jobCode}>[{option.code}]</p>
        <span className={classes.divider}>-</span>
        <p className={classes.jobTitle}>
          {option.title}
          {jobStatusLabel ? ` (${jobStatusLabel})` : ''}
        </p>
      </div>
      <div className={classes.bodyOption}>
        {userRecruiter && (
          <div className={classes.containerUserRecruiter}>
            <Avatar
              name={userRecruiter && userRecruiter?.username ? userRecruiter?.username : ''}
              email={userRecruiter && userRecruiter?.email ? userRecruiter?.userRecruiter : ''}
              src={avatarSrc}
              maxInitials={2}
              color={CONST.APP_COLORS['primary-light']}
              className={classes.avatar}
              size="1rem"
              round={true}
            />
            <div className={classes.textCompanyAreaAndLocation}>
              {userRecruiter && userRecruiter?.username ? userRecruiter?.username : ''}
            </div>
            {(() => {
              const isAgent = userRecruiter && userRecruiter.agent === true;

              return isAgent ? (
                <Box ml={0.5} display="flex" alignItems="center">
                  <AISuggestionsIcon size={16} variant="outlined" />
                </Box>
              ) : null;
            })()}
          </div>
        )}
        {option?.userRecruiter && companyAreaName ? (
          <span
            className={classes.circle}
            style={{
              margin: '0 4px',
              fontWeight: 800
            }}
          >
            &bull;
          </span>
        ) : null}
        {companyAreaName ? (
          <div style={{ maxWidth: option.address ? '50%' : '100%' }}>
            <p className={classes.textCompanyAreaAndLocation}>{companyAreaName}</p>
          </div>
        ) : null}
        {option?.address && (userRecruiter || companyAreaName) ? (
          <span
            className={classes.circle}
            style={{
              margin: '0 4px',
              fontWeight: 800
            }}
          >
            &bull;
          </span>
        ) : null}
        {option?.address && (
          <div style={{ maxWidth: option.address ? '50%' : '100%' }}>
            <p className={classes.textCompanyAreaAndLocation}>{addressFormatted}</p>
          </div>
        )}
      </div>
    </div>
  );
});

RenderOption.displayName = 'RenderOption';

const areEqual = (prevProps, nextProps) => {
  const prevId = prevProps.option?.id || prevProps.option?.value;
  const nextId = nextProps.option?.id || nextProps.option?.value;

  return prevId === nextId;
};

const MemoizedRenderOption = React.memo(RenderOption, areEqual);

class AutoCompleteJobSelect extends React.Component {
  constructor(props) {
    super(props);
    this.lastSelectedValue = null;
    this.isHandlingChange = false;
    this.labelCache = new Map();
    this.getStatusLabel = this.getStatusLabel.bind(this);
    this.getOptionLabel = this.getOptionLabel.bind(this);
    this.getOptionSelected = this.getOptionSelected.bind(this);
    this.handleChangeSelectJob = this.handleChangeSelectJob.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.jobsList !== this.props.jobsList && this.props.jobsList) {
      this.labelCache.clear();

      const jobsToCache = this.props.jobsList.slice(0, 100);
      jobsToCache.forEach((job) => {
        const jobId = job.id || job.value;
        if (jobId && !this.labelCache.has(jobId)) {
          const statusLabel = this.getStatusLabel(job?.status);
          const statusSuffix = statusLabel ? ` (${statusLabel})` : '';
          const label = `[${job.code || ''}] - ${job.title || ''}${statusSuffix}`;
          this.labelCache.set(jobId, label);
        }
      });
    }
  }

  getStatusLabel = (status) => {
    if (!status) return '';

    return this.props.intl.formatMessage({
      defaultMessage: status,
      id: `JobsList.status_${status}`
    });
  };

  getOptionLabel = (option) => {
    if (!option || typeof option === 'string') return option || '';

    const optionId = option.id || option.value;
    if (optionId && this.labelCache.has(optionId)) {
      return this.labelCache.get(optionId);
    }

    const statusLabel = this.getStatusLabel(option?.status);
    const statusSuffix = statusLabel ? ` (${statusLabel})` : '';

    const label = `[${option.code || ''}] - ${option.title || ''}${statusSuffix}`;

    if (optionId) {
      this.labelCache.set(optionId, label);
    }

    return label;
  };

  getOptionSelected = (option, value) => {
    if (!option || !value) return false;

    const optionId = option.id || option.value;
    const valueId = value.id || value.value;

    return optionId === valueId;
  };

  filterOptions = (options, { inputValue }) => {
    if (!inputValue || inputValue.trim() === '') {
      return (options || []).slice(0, 100);
    }

    const filtered = (options || []).filter((option) => {
      const label = this.getOptionLabel(option).toLowerCase();
      const search = inputValue.toLowerCase();
      return label.includes(search);
    });

    return filtered.slice(0, 100);
  };

  handleChangeSelectJob = (event, selected) => {
    if (this.isHandlingChange) {
      return;
    }

    const selectedId = selected?.id || selected?.value;
    const lastSelectedId = this.lastSelectedValue?.id || this.lastSelectedValue?.value;

    if (selectedId === lastSelectedId) {
      return;
    }

    this.isHandlingChange = true;
    this.lastSelectedValue = selected;

    requestAnimationFrame(() => {
      if (this.props.handleSelectJob) {
        this.props.handleSelectJob(selected);
      }
      this.isHandlingChange = false;
    });
  };

  render() {
    const { classes, placeholder, intl, jobsList, loading, value } = this.props;

    return (
      <div>
        <Autocomplete
          id={this.props.id || 'filterJobs'}
          name={this.props.id || 'filterJobs'}
          value={value || null}
          options={jobsList || []}
          getOptionLabel={this.getOptionLabel}
          getOptionSelected={this.getOptionSelected}
          filterOptions={this.filterOptions}
          renderOption={(option) => (
            <MemoizedRenderOption
              key={option.id || option.value}
              option={option}
              classes={classes}
              getStatusLabel={this.getStatusLabel}
              countries={this.props.countries}
              intl={intl}
            />
          )}
          classes={{
            listbox: classes.listbox,
            popper: classes.popper,
            option: classes.option
          }}
          onChange={this.handleChangeSelectJob}
          fullWidth
          filterSelectedOptions
          disableListWrap
          clearOnBlur={false}
          popupIcon={<ExpandMoreIcon fontSize="small" />}
          ListboxProps={{
            style: { maxHeight: '400px', overflowY: 'auto' },
            onScroll: (e) => {
              e.stopPropagation();
            }
          }}
          loading={loading}
          loadingText={intl.formatMessage(messages.loading)}
          noOptionsText={intl.formatMessage(messages.noOptions)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                placeholder ||
                intl.formatMessage({
                  id: 'SelectJob.placeholder_selectJob'
                })
              }
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                style: { height: '36px', padding: '0.5rem 1rem', borderRadius: '10rem' },
                className: classes.inputRoot,
                startAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.startAdornment}
                  </React.Fragment>
                )
              }}
            />
          )}
        />
      </div>
    );
  }
}

AutoCompleteJobSelect.propTypes = {
  countries: []
};

const mapStateToProps = (state) => ({
  countries: state.countries.countries
});

export default connect(
  mapStateToProps,
  null
)(injectIntl(withStyles(useStyles)(AutoCompleteJobSelect)));
